// Roles & access — live data. Three reads:
//  • getRoles   → GET /roles      : the created roles (list)
//  • getModules → GET /modules    : the complete capability matrix (all modules + permissions)
//  • getRole    → GET /roles/:id  : one role's enabled permissions (populates the matrix)
// Types are modelled directly on the backend responses.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

export type Verb = 'view' | 'create' | 'edit' | 'delete';
export const VERBS: Verb[] = ['view', 'create', 'edit', 'delete'];
export type Platform = 'web' | 'mobile'; // module type admin → web, user → mobile
export type ModuleType = 'admin' | 'user';

// ---- domain types (from the responses) ----
export interface Permission {
  id: string;
  slug: string;
  title: string;
}
export interface MatrixModule {
  id: string; // module _id — the create/update payloads group permissions by it
  slug: string;
  title: string;
  category: string;
  verbs: Permission[]; // core columns (slug ∈ VERBS), ordered view→delete
  extras: Permission[]; // everything else → action pills
}
export interface ModuleMatrix {
  web: MatrixModule[];
  mobile: MatrixModule[];
}

export interface RoleModuleRef {
  title: string;
  type: ModuleType;
}
export interface RoleListItem {
  id: string;
  name: string;
  description: string;
  isOwner: boolean;
  isDefault: boolean; // built-in/system role
  module: RoleModuleRef[];
  adminPermission: number; // web permission count
  userPermission: number; // mobile permission count
  totalUsers: number;
}

export interface RolePermissionGroup {
  moduleId: { _id: string; slug: string; title: string; type: ModuleType };
  permissions: { _id: string; slug: string; title: string }[]; // enabled only
}

/** Lightweight role option for pickers (GET /roles/selection). */
export interface RoleOption {
  id: string;
  title: string;
}

/** Per-role permission definitions (GET /roles/selection/detailed) — same group
 *  shape as RoleDetail, without the isOwner/isDefault flags. */
export interface RoleSelectionDetail {
  id: string;
  name: string;
  description: string;
  adminPermissions: RolePermissionGroup[]; // web
  userPermissions: RolePermissionGroup[]; // mobile
}
export interface RoleDetail {
  id: string;
  name: string;
  description: string;
  isOwner: boolean;
  isDefault: boolean;
  adminPermissions: RolePermissionGroup[]; // web
  userPermissions: RolePermissionGroup[]; // mobile
}

// ---- raw API shapes (mapped below) ----
interface ApiPermission {
  _id: string;
  slug: string;
  title: string;
}
interface ApiModule {
  _id: string;
  category: string;
  slug: string;
  title: string;
  type: ModuleType;
  permissions: ApiPermission[];
}
interface ApiRoleListItem extends Omit<RoleListItem, 'id'> {
  _id: string;
}
interface ApiRoleDetail extends Omit<RoleDetail, 'id'> {
  _id: string;
}
interface ApiRoleSelectionDetail extends Omit<RoleSelectionDetail, 'id'> {
  _id: string;
}

const coreVerbs = new Set<string>(VERBS);
const toPermission = (p: ApiPermission): Permission => ({
  id: p._id,
  slug: p.slug,
  title: p.title,
});

const toMatrixModule = (m: ApiModule): MatrixModule => ({
  id: m._id,
  slug: m.slug,
  title: m.title,
  category: m.category,
  verbs: m.permissions
    .filter((p) => coreVerbs.has(p.slug))
    .sort((a, b) => VERBS.indexOf(a.slug as Verb) - VERBS.indexOf(b.slug as Verb))
    .map(toPermission),
  extras: m.permissions.filter((p) => !coreVerbs.has(p.slug)).map(toPermission),
});

/** Set of enabled permission ids across both platforms. Permission `_id`s are
 *  globally unique (a module's view on web and on mobile are different ids). */
export const enabledSet = (detail?: RoleDetail): Set<string> => {
  const set = new Set<string>();
  const add = (groups?: RolePermissionGroup[]) =>
    groups?.forEach((g) => g.permissions.forEach((p) => set.add(p._id)));
  add(detail?.adminPermissions);
  add(detail?.userPermissions);
  return set;
};

// ---- create / update ----

export interface RolePayload {
  name: string;
  description: string;
  adminPermissions: { moduleId: string; permissions: string[] }[];
  userPermissions: { moduleId: string; permissions: string[] }[];
}

/** Group the selected permission ids by module, split web→admin / mobile→user. */
export const buildRolePayload = (
  catalog: ModuleMatrix,
  selected: Set<string>,
  name: string,
  description: string,
): RolePayload => {
  const groups = (modules: MatrixModule[]) =>
    modules
      .map((m) => ({
        moduleId: m.id,
        permissions: [...m.verbs, ...m.extras].filter((p) => selected.has(p.id)).map((p) => p.id),
      }))
      .filter((g) => g.permissions.length > 0);
  return {
    name: name.trim(),
    description: description.trim(),
    adminPermissions: groups(catalog.web),
    userPermissions: groups(catalog.mobile),
  };
};

/** UI-level module nesting: child slug → parent slug. A child renders as an
 *  indented sub-row under its parent in the capability matrix, and any of its
 *  permissions requires the parent's `view`. Workflow's backend slug is
 *  CONFIRMED singular (`workflow`, per the GET /modules payload). */
export const MODULE_PARENT_SLUG: Record<string, string> = {
  workflow: 'forms_templates',
};

/** Toggle a permission with the matrix cascade rules (within one module):
 *  verb ON enables every lower verb (view←create←edit←delete); verb OFF
 *  disables it and every higher verb — view OFF also clears the extras and
 *  every permission of the modules nested under it (MODULE_PARENT_SLUG);
 *  extra ON enables view; extra OFF is just itself. Turning anything ON in a
 *  nested module also enables its parent's view. `allModules` is the
 *  same-platform module list the cross-module rules resolve against. */
export const toggleWithCascade = (
  selected: Set<string>,
  module: MatrixModule,
  permId: string,
  allModules: MatrixModule[] = [],
): Set<string> => {
  const next = new Set(selected);
  const verbIndex = module.verbs.findIndex((p) => p.id === permId);

  if (verbIndex >= 0) {
    if (next.has(permId)) {
      // OFF: this verb + everything above it; view off clears the extras too,
      // plus everything in the modules nested under this one.
      module.verbs.slice(verbIndex).forEach((p) => next.delete(p.id));
      if (verbIndex === 0) {
        module.extras.forEach((p) => next.delete(p.id));
        allModules
          .filter((m) => MODULE_PARENT_SLUG[m.slug] === module.slug)
          .forEach((m) => [...m.verbs, ...m.extras].forEach((p) => next.delete(p.id)));
      }
    } else {
      // ON: this verb + everything below it.
      module.verbs.slice(0, verbIndex + 1).forEach((p) => next.add(p.id));
    }
  } else if (next.has(permId)) {
    // Extra pill OFF is just itself.
    next.delete(permId);
  } else {
    next.add(permId);
    if (module.verbs[0]) next.add(module.verbs[0].id); // extras require view
  }

  // Nested module: any permission turning ON also requires the parent's view.
  const parentSlug = MODULE_PARENT_SLUG[module.slug];
  if (parentSlug && next.has(permId)) {
    const parentView = allModules.find((m) => m.slug === parentSlug)?.verbs[0];
    if (parentView) next.add(parentView.id);
  }
  return next;
};

export const rolesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getRoles: build.query<RoleListItem[], void>({
      query: () => ({ ...ROUTES.roles.list }),
      transformResponse: (res: ApiRoleListItem[]) =>
        (res ?? []).map((r) => ({ ...r, id: r._id, module: r.module ?? [] })),
      // LIST tag: mutations target the list without touching per-id detail tags
      // (a bare 'Roles' invalidation would refetch a deleted role's detail).
      providesTags: [{ type: 'Roles', id: 'LIST' }],
    }),
    // Role options for pickers — flat {id, title}.
    getRolesSelection: build.query<RoleOption[], void>({
      query: () => ({ ...ROUTES.roles.selection }),
      transformResponse: (res: RoleOption[]) => res ?? [],
      providesTags: [{ type: 'Roles', id: 'SELECTION' }],
    }),
    // Per-role permission definitions — for recomputing effective access.
    getRolesSelectionDetailed: build.query<RoleSelectionDetail[], void>({
      query: () => ({ ...ROUTES.roles.selectionDetailed }),
      transformResponse: (res: ApiRoleSelectionDetail[]) =>
        (res ?? []).map((r) => ({
          id: r._id,
          name: r.name,
          description: r.description,
          adminPermissions: r.adminPermissions ?? [],
          userPermissions: r.userPermissions ?? [],
        })),
      providesTags: [{ type: 'Roles', id: 'SELECTION_DETAILED' }],
    }),
    getModules: build.query<ModuleMatrix, void>({
      query: () => ({ ...ROUTES.modules.list }),
      transformResponse: (res: ApiModule[]) => ({
        web: (res ?? []).filter((m) => m.type === 'admin').map(toMatrixModule),
        mobile: (res ?? []).filter((m) => m.type === 'user').map(toMatrixModule),
      }),
      providesTags: ['Modules'],
    }),
    getRole: build.query<RoleDetail, string>({
      query: (id) => ({ ...ROUTES.roles.byId(id) }),
      transformResponse: (res: ApiRoleDetail) => ({
        ...res,
        id: res._id,
        adminPermissions: res.adminPermissions ?? [],
        userPermissions: res.userPermissions ?? [],
      }),
      providesTags: (_res, _err, id) => [{ type: 'Roles', id }],
    }),
    createRole: build.mutation<{ _id?: string }, RolePayload>({
      query: (body) => ({ ...ROUTES.roles.create, data: body }),
      invalidatesTags: [
        { type: 'Roles', id: 'LIST' },
        { type: 'Roles', id: 'SELECTION' },
        { type: 'Roles', id: 'SELECTION_DETAILED' },
      ],
    }),
    updateRole: build.mutation<unknown, { id: string; body: RolePayload }>({
      query: ({ id, body }) => ({ ...ROUTES.roles.update(id), data: body }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Roles', id: 'LIST' },
        { type: 'Roles', id },
        { type: 'Roles', id: 'SELECTION' },
        { type: 'Roles', id: 'SELECTION_DETAILED' },
      ],
    }),
    deleteRole: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.roles.remove(id) }),
      // List only — invalidating the id tag would refetch the deleted detail.
      invalidatesTags: [
        { type: 'Roles', id: 'LIST' },
        { type: 'Roles', id: 'SELECTION' },
        { type: 'Roles', id: 'SELECTION_DETAILED' },
      ],
    }),
  }),
});

export const {
  useGetRolesQuery,
  useGetRolesSelectionQuery,
  useGetRolesSelectionDetailedQuery,
  useGetModulesQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} = rolesApi;
